import { Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { Constants } from 'app/constants/constants';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-project-about',
  templateUrl: './project-about.component.html',
  styleUrls: ['./project-about.component.scss'],
  standalone: false,
})
export class ProjectAboutComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() itemToShow;

  public readonly projectAboutItems = [
    {
      id: 'analysis',
      title: 'Analyses',
      description:
        'Analyses are the results of natural language processing. We use analyses in Spotlight to power most data visualizations',
      linkText: 'Learn more about Analyses',
      href: 'https://spotlight-docs.lexalytics.com/docs/running-analyses',
      icon: Constants.ANALYSIS_ICON,
      iconColor: Constants.ANALYSIS_ICON_COLOR,
    },
    {
      id: 'group',
      title: 'Analysis Groups',
      description:
        'Analysis groups allow you to collect different analysis results into one location for visualization on dashboards',
      linkText: 'Learn more about Analysis Groups',
      href: 'https://spotlight-docs.lexalytics.com/docs/running-analyses#analysis-groups',
      icon: Constants.GROUP_ICON,
      iconColor: Constants.GROUP_ICON_COLOR,
    },
    {
      id: 'collection',
      title: 'Collections',
      description:
        'Collections are where you store all your documents and responses. You can analyze a collection to create Natural Language Processing (NLP) results which can then be used to visualize trends in your dashboards',
      linkText: 'Learn more about Collections',
      href: 'https://spotlight-docs.lexalytics.com/docs/collection-upload',
      icon: Constants.COLLECTION_ICON,
      iconColor: Constants.COLLECTION_ICON_COLOR,
    },
    {
      id: 'dashboard',
      title: 'Dashboards',
      description:
        'Dashboards are where your data visualizations live. You can use collections, analyses, or analysis groups to power a dashboard',
      linkText: 'Learn more about Dashboards',
      href: 'https://spotlight-docs.lexalytics.com/docs/working-with-dashboards',
      icon: Constants.DASHBOARD_ICON,
      iconColor: Constants.DASHBOARD_ICON_COLOR,
    },
  ];

  projectItemsToShow = this.projectAboutItems;
  title = 'Projects';
  defaultTitle = 'Projects';

  private translocoService = inject(TranslocoService);
  private unsub$ = new Subject<void>();

  ngOnInit(): void {
    this.translocoService
      .selectTranslateObject([
        'project-about.analysis',
        'project-about.group',
        'project-about.collection',
        'project-about.dashboard',
      ])
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        translations.forEach((t, idx) => {
          this.projectAboutItems[idx].title = t.title;
          this.projectAboutItems[idx].description = t.description;
          this.projectAboutItems[idx].linkText = t['link-text'];
        });

        this.selectItemsToShow();
      });

    this.translocoService
      .selectTranslate('project-about.projects')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translation) => {
        this.defaultTitle = translation;
        if (this.title === 'Projects') {
          this.title = translation;
        }
      });
  }

  ngOnChanges(): void {
    this.selectItemsToShow();
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private selectItemsToShow() {
    this.projectItemsToShow = this.projectAboutItems.filter((item) => item.id === this.itemToShow);
    this.title = this.projectItemsToShow[0]?.title;

    if (!this.projectItemsToShow.length) {
      this.projectItemsToShow = this.projectAboutItems;
      this.title = this.defaultTitle;
    }
  }
}
